import { Controller } from "@hotwired/stimulus"

/**
 * Controller for managing bulk evaluation creation form
 * Handles classroom and month selection with "select all" functionality
 */
export default class extends Controller {
  static targets = [
    "classroomsList",
    "classroomCheckbox",
    "monthCheckbox",
    "classroomCount",
    "monthCount",
    "submitButton",
    "perMonthField"
  ]

  static values = {
    submittingText: { type: String, default: "Création en cours..." }
  }

  connect() {
    this.updateCounts()
    this.originalButtonContent = this.hasSubmitButtonTarget ? this.submitButtonTarget.innerHTML : ''
  }

  /**
   * Toggles all classroom checkboxes
   * @param {Event} event - The change event from the "select all" checkbox
   */
  toggleAllClassrooms(event) {
    const checked = event.target.checked
    this.classroomCheckboxTargets.forEach(checkbox => {
      checkbox.checked = checked
    })
    this.updateCounts()
  }

  /**
   * Au secondaire : le nombre par mois ne vaut que pour les contrôles continus
   * @param {Event} event - The change event from a type radio
   */
  toggleEvaluationType(event) {
    if (!this.hasPerMonthFieldTarget) return

    this.perMonthFieldTarget.classList.toggle("hidden", event.target.value === "composition")
  }

  /**
   * Toggles all pedagogic month checkboxes
   * @param {Event} event - The change event from the "select all" checkbox
   */
  toggleAllMonths(event) {
    const checked = event.target.checked
    this.monthCheckboxTargets.forEach(checkbox => {
      checkbox.checked = checked
    })
    this.updateCounts()
  }

  /**
   * Updates the display counts for selected classrooms and months
   */
  updateCounts() {
    const classroomCount = this.classroomCheckboxTargets.filter(c => c.checked).length
    const monthCount = this.monthCheckboxTargets.filter(c => c.checked).length

    if (this.hasClassroomCountTarget) {
      this.classroomCountTarget.textContent = classroomCount
    }

    if (this.hasMonthCountTarget) {
      this.monthCountTarget.textContent = monthCount
    }

    this.updateSubmitButton(classroomCount, monthCount)
  }

  /**
   * Updates the submit button state based on selection
   * @param {number} classroomCount - Number of selected classrooms
   * @param {number} monthCount - Number of selected months
   */
  updateSubmitButton(classroomCount, monthCount) {
    if (this.hasSubmitButtonTarget) {
      const isValid = classroomCount > 0 && monthCount > 0
      this.submitButtonTarget.disabled = !isValid

      if (isValid) {
        this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      } else {
        this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
      }
    }
  }

  /**
   * Handles form submission - shows spinner on button
   * @param {Event} event - The submit event
   */
  submitForm(event) {
    if (!this.hasSubmitButtonTarget) return

    const button = this.submitButtonTarget

    // Disable button to prevent double submission
    button.disabled = true
    button.classList.add('opacity-75', 'cursor-wait')

    // Show spinner with loading text
    button.innerHTML = `
      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      ${this.submittingTextValue}
    `
  }

  /**
   * Resets the submit button to its original state
   * Called if form submission fails or is cancelled
   */
  resetButton() {
    if (!this.hasSubmitButtonTarget) return

    const button = this.submitButtonTarget
    button.disabled = false
    button.classList.remove('opacity-75', 'cursor-wait')
    button.innerHTML = this.originalButtonContent
    this.updateCounts()
  }
}
